import { of, Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { Injector } from '@angular/core';
import { CompaniesApi, CompaniesApiClient } from '@sg/api/companies-api';
import { SgEntityFetchAdapter } from '../components/entity-select-auto-complete';

declare module '../components/entity-select-auto-complete/entity-select-fetch-store.service' {
  export interface SgEntitySelectEntityFetchMap {
    Company: {
      entity: CompaniesApi.CompanyDto;
      query: CompaniesApi.CompanyDto;
    };
    Company_ByName: {
      entity: CompaniesApi.CompanyDto;
      query: CompaniesApi.CompanyDto;
    };
  }
}

const MAX_CACHE_AGE = 1000 * 60 * 2; // 2 minutes
const CACHE: {
  companies?: CompaniesApi.CompanyDto[];
  companies$?: Observable<CompaniesApi.CompanyDto[]>;
  cacheAge?: number;
} = {};

abstract class BaseCompanyApiToEntityFetcherLink<T extends 'Company' | 'Company_ByName'>
  implements SgEntityFetchAdapter<T>
{
  abstract readonly key: T;
  abstract readonly metadata: {
    idField: keyof CompaniesApi.CompanyDto;
    labelField: keyof CompaniesApi.CompanyDto;
    queryField?: { field: any; isArray?: boolean };
  };

  protected readonly companiesApi: CompaniesApiClient;
  private get cachedCompanies() {
    return CACHE.companies;
  }
  private set cachedCompanies(value: CompaniesApi.CompanyDto[]) {
    CACHE.companies = value;
  }

  private get cachedCompanies$() {
    return CACHE.companies$;
  }
  private set cachedCompanies$(value: Observable<CompaniesApi.CompanyDto[]>) {
    CACHE.companies$ = value;
  }

  private get cacheAge(): number {
    return CACHE.cacheAge;
  }
  private set cacheAge(value: number) {
    CACHE.cacheAge = value;
  }

  constructor(injector: Injector) {
    this.companiesApi = injector.get(CompaniesApiClient);
  }

  abstract getEntity(id: string): Observable<CompaniesApi.CompanyDto>;

  findEntities(query?: CompaniesApi.CompanyDto) {
    return this.getCompanies().pipe(
      map((results) => {
        if (query) {
          for (const k of Object.keys(query)) {
            let value = query[k];
            if (typeof value === 'string') {
              value = value.toLowerCase();
              results = results.filter((r) => (r[k] || '').toLowerCase().indexOf(value) > -1);
            } else {
              results = results.filter((r) => r[k] === query[k]);
            }
          }
        }
        return { data: results };
      })
    );
  }

  protected getCompanies() {
    if (this.cachedCompanies && Date.now() - this.cacheAge < MAX_CACHE_AGE) {
      return of(this.cachedCompanies);
    } else if (this.cachedCompanies$) {
      return this.cachedCompanies$;
    } else {
      return (this.cachedCompanies$ = this.companiesApi.companiesControllerGetCompanies({}, { observe: 'body' }).pipe(
        tap((companies) => {
          this.cacheAge = Date.now();
          this.cachedCompanies$ = undefined;
          this.cachedCompanies = companies;
        })
      ));
    }
  }
}

export class CompanyApiToEntityFetcherLink extends BaseCompanyApiToEntityFetcherLink<'Company'> {
  readonly key: 'Company' = 'Company';
  readonly metadata: {
    idField: keyof CompaniesApi.CompanyDto;
    labelField: keyof CompaniesApi.CompanyDto;
    queryField?: { field: any; isArray?: boolean };
  } = {
    idField: 'id',
    labelField: 'extCompanyCode',
    queryField: { field: 'extCompanyCode' },
  };

  constructor(injector: Injector) {
    super(injector);
  }

  getEntity(id: string) {
    return this.companiesApi.companiesControllerGetCompaniesByid({ id }, { observe: 'body' });
  }
}

export class CompanyByNameApiToEntityFetcherLink extends BaseCompanyApiToEntityFetcherLink<'Company_ByName'> {
  readonly key: 'Company_ByName' = 'Company_ByName';
  readonly metadata: {
    idField: keyof CompaniesApi.CompanyDto;
    labelField: keyof CompaniesApi.CompanyDto;
    queryField?: { field: any; isArray?: boolean };
  } = {
    idField: 'extCompanyCode',
    labelField: 'extCompanyCode',
    queryField: { field: 'extCompanyCode' },
  };

  constructor(injector: Injector) {
    super(injector);
  }

  getEntity(id: string) {
    return this.getCompanies().pipe(map((companies) => companies.find((c) => c.extCompanyCode === id)));
  }
}
